/*
  VS BOLD base: behaviour for vsb- sections, loaded by the sections themselves
  (snippets/vsb-assets.liquid). Safe to load more than once.

  <d-tracklist>  audio previews: one plays at a time; its waveform fills as it plays,
                 and a tap on the waveform jumps there.
  <d-sticky>     a buy bar, shown only while none of the page's buy buttons is on screen.
  <d-reveal>     shows the rest of a list when its button is pressed.
  <d-gallery>    product media: thumbnails swap the main picture or video.
  <d-variants>   a plain variant menu that keeps the buy form's variant id in step.
  Order properties: the product section's "Order property" blocks, added to
  every buy form for that product on the page (hero, tracklist, last call,
  sticky bar), so each add to cart records the same bonuses and gifts.
  vsbWhenNear(el, init)  runs a section's set-up once it comes near the
                 screen (IntersectionObserver), so the sections far down a long
                 page cost nothing until someone scrolls to them.
*/

if (!window.vsbWhenNear) {
  window.vsbWhenNear = function whenNear(el, init, margin = '600px 0px') {
    if (!window.IntersectionObserver) { init(); return; }
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      init();
    }, { rootMargin: margin });
    io.observe(el);
  };
}

if (!customElements.get('d-tracklist')) {
  customElements.define('d-tracklist', class extends HTMLElement {
    connectedCallback() {
      this.audio = new Audio();
      this.audio.preload = 'none';
      this.tracks = [...this.querySelectorAll('.d-track[data-src]')];
      this.current = null;
      this.tracks.forEach((t) => {
        t.querySelector('.d-track__toggle')?.addEventListener('click', () => this.toggle(t));
        // The waveform seeks: a tap at 40% of it plays from 40% of the clip.
        t.querySelector('.d-track__wave')?.addEventListener('click', (e) => {
          const r = e.currentTarget.getBoundingClientRect();
          this.seek(t, Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)));
        });
      });
      this.audio.addEventListener('timeupdate', () => {
        const d = this.audio.duration;
        if (this.current && d) this.current.style.setProperty('--progress', this.audio.currentTime / d);
      });
      this.audio.addEventListener('ended', () => this.setPlaying(false));
      // One player on the page at a time.
      document.addEventListener('d:play', (e) => { if (e.detail !== this) this.pause(); });
      this.drawMissingWaveforms();
    }

    select(track) {
      if (this.current === track) return;
      this.tracks.forEach((t) => {
        t.classList.toggle('is-on', t === track);
        t.querySelector('.d-track__toggle')?.setAttribute('aria-pressed', String(t === track));
      });
      this.current = track;
      this.audio.src = track.dataset.src;
      track.style.setProperty('--progress', 0);
    }

    toggle(track) {
      if (this.current === track && !this.audio.paused) { this.pause(); return; }
      this.select(track);
      this.play();
    }

    seek(track, fraction) {
      this.select(track);
      const go = () => { this.audio.currentTime = fraction * this.audio.duration; };
      if (this.audio.duration) go();
      else this.audio.addEventListener('loadedmetadata', go, { once: true });
      track.style.setProperty('--progress', fraction);
      this.play();
    }

    play() {
      document.dispatchEvent(new CustomEvent('d:play', { detail: this }));
      this.audio.play().then(() => this.setPlaying(true)).catch((e) => { this.lastError = e; this.setPlaying(false); });
    }

    pause() { this.audio.pause(); this.setPlaying(false); }

    setPlaying(on) {
      this.dataset.playing = String(on);
      this.tracks.forEach((t) => t.classList.toggle('is-playing', on && t === this.current));
    }

    // Clips added without a stored waveform: decode them once the list is on
    // screen, and draw the same bars the stored ones get.
    drawMissingWaveforms() {
      const missing = this.tracks.filter((t) => !t.querySelector('.d-wave'));
      if (!missing.length || !window.IntersectionObserver) return;
      const io = new IntersectionObserver(async (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        const ctx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, 1, 44100);
        for (const t of missing) {
          try {
            const buf = await ctx.decodeAudioData(await (await fetch(t.dataset.src)).arrayBuffer());
            const data = buf.getChannelData(0), bars = 96, size = Math.floor(data.length / bars), peaks = [];
            for (let i = 0; i < bars; i++) {
              let sum = 0;
              for (let j = i * size; j < (i + 1) * size; j++) sum += data[j] * data[j];
              peaks.push(Math.sqrt(sum / size));
            }
            const top = Math.max(...peaks) || 1;
            const d = peaks.map((v, i) => { const h = Math.max(5, (v / top) * 92); return `M${i * 10} ${(100 - h) / 2}h6v${h}h-6z`; }).join('');
            const svg = (cls) => `<svg class="${cls}" viewBox="0 0 ${bars * 10} 100" preserveAspectRatio="none" data-bars="${bars}"><path d="${d}"/></svg>`;
            t.querySelector('.d-track__wave').innerHTML = svg('d-wave') + svg('d-wave d-wave--played');
          } catch (e) { /* no waveform for this one; it still plays */ }
        }
      });
      io.observe(this);
    }
  });
}

if (!customElements.get('d-sticky')) {
  customElements.define('d-sticky', class extends HTMLElement {
    connectedCallback() {
      this.onScreen = new Set();
      this.targets = [...document.querySelectorAll('.d-buy')].filter((el) => !this.contains(el));
      if (!this.targets.length) return;
      this.observer = new IntersectionObserver((entries) => {
        for (const e of entries) e.isIntersecting ? this.onScreen.add(e.target) : this.onScreen.delete(e.target);
        this.update();
      });
      this.targets.forEach((t) => this.observer.observe(t));
      // Past the first buy button can happen with no intersection change at all
      // (a jump, a restored scroll), so scrolling re-checks too.
      this.onScroll = () => { if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); }); };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
    update() { this.dataset.visible = String(this.onScreen.size === 0 && this.targets[0].getBoundingClientRect().bottom < 0); }
    disconnectedCallback() { this.observer?.disconnect(); window.removeEventListener('scroll', this.onScroll); }
  });
}

if (!customElements.get('d-reveal')) {
  customElements.define('d-reveal', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('.d-reveal__more button')?.addEventListener('click', () => this.removeAttribute('data-collapsed'));
    }
  });
}

if (!customElements.get('d-gallery')) {
  customElements.define('d-gallery', class extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('.d-pdp__main [data-stage]');
      this.thumbs = [...this.querySelectorAll('[data-media]')];
      this.thumbs.forEach((b) => b.addEventListener('click', () => this.show(b)));
    }
    show(button) {
      this.thumbs.forEach((b) => b.setAttribute('aria-current', String(b === button)));
      const tpl = button.querySelector('template');
      if (tpl) this.main.replaceChildren(tpl.content.cloneNode(true));
    }
  });
}

if (!customElements.get('d-variants')) {
  customElements.define('d-variants', class extends HTMLElement {
    connectedCallback() {
      const select = this.querySelector('select');
      select?.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        document.querySelectorAll(`form[data-product-id="${this.dataset.productId}"] input[name="id"]`).forEach((i) => (i.value = select.value));
        const id = this.dataset.productId;
        document.querySelectorAll(`[data-price-for="${id}"]`).forEach((el) => (el.textContent = option.dataset.price));
        // The struck-through price and the "% off" badge follow the variant (snippets/vsb-price).
        const off = Number(option.dataset.off || 0);
        document.querySelectorAll(`[data-compare-for="${id}"]`).forEach((el) => {
          el.hidden = !off;
          if (off) el.lastChild.textContent = option.dataset.compare;
        });
        document.querySelectorAll(`[data-off-for="${id}"]`).forEach((el) => {
          el.hidden = !off;
          const n = el.querySelector('[data-off-n]');
          if (n) n.textContent = off;
        });
        const url = new URL(location.href);
        url.searchParams.set('variant', select.value);
        history.replaceState({}, '', url);
      });
    }
  });
}

if (!window.vsbOrderProperties) {
  window.vsbOrderProperties = function apply() {
    document.querySelectorAll('script[data-vsb-properties]').forEach((script) => {
      let properties;
      try {
        properties = JSON.parse(script.textContent);
      } catch (e) {
        return;
      }
      document.querySelectorAll(`form[data-product-id="${script.dataset.productId}"]`).forEach((form) => {
        for (const [name, value] of Object.entries(properties)) {
          const field = `properties[${name}]`;
          if ([...form.elements].some((el) => el.name === field)) continue;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = field;
          input.value = value;
          form.appendChild(input);
        }
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', window.vsbOrderProperties);
  else window.vsbOrderProperties();
}
